import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Button, Segment } from 'semantic-ui-react';
import SidebarPortal from '@plone/volto/components/manage/Sidebar/SidebarPortal';
import InlineForm from '@plone/volto/components/manage/Form/InlineForm';
import { ImageInput } from '@plone/volto/components/manage/Widgets/ImageWidget';
import { flattenToAppURL, isInternalURL } from '@plone/volto/helpers/Url/Url';
import { withBlockExtensions } from '@plone/volto/helpers/Extensions';
import config from '@plone/volto/registry';
import { ImageGallerySchema } from './Schema';
import './ImageGallery.css';

const messages = defineMessages({
  imageGallery: {
    id: 'imageGalleryTitle',
    defaultMessage: 'Image Gallery',
  },
  imageCount: {
    id: 'imageGalleryCount',
    defaultMessage: '{count, plural, one {# image} other {# images}}',
  },
  images: {
    id: 'imageGalleryImages',
    defaultMessage: 'Images',
  },
  clickToAddCaption: {
    id: 'imageGalleryClickCaption',
    defaultMessage: 'Click to add caption...',
  },
  captionPlaceholder: {
    id: 'imageGalleryCaptionPlaceholder',
    defaultMessage: 'Caption...',
  },
  moveLeft: {
    id: 'imageGalleryMoveLeft',
    defaultMessage: 'Move left',
  },
  moveRight: {
    id: 'imageGalleryMoveRight',
    defaultMessage: 'Move right',
  },
  moveUp: {
    id: 'imageGalleryMoveUp',
    defaultMessage: 'Move up',
  },
  moveDown: {
    id: 'imageGalleryMoveDown',
    defaultMessage: 'Move down',
  },
  removeImage: {
    id: 'imageGalleryRemove',
    defaultMessage: 'Remove',
  },
  imageN: {
    id: 'imageGalleryImageN',
    defaultMessage: 'Image {n}',
  },
});

const ImageGalleryEdit = (props) => {
  const { data, block, selected, onChangeBlock } = props;
  const intl = useIntl();
  const images = data.images || [];
  const columns = data.columns || 3;
  const gap = data.gap || '16px';
  const [editingCaption, setEditingCaption] = useState(null);
  const Image = config.getComponent({ name: 'Image' }).component;

  const updateImages = (newImages) => {
    onChangeBlock(block, { ...data, images: newImages });
  };

  const handleAddImage = (id, image, meta = {}) => {
    const url = Array.isArray(image)
      ? image?.[0]?.['@id']
      : image
        ? image['@id'] || image
        : '';

    if (!url) return;

    const newImage = {
      url: flattenToAppURL(url),
      image_field: meta.image_field,
      image_scales: meta.image_scales,
      alt: meta.title || '',
      caption: '',
    };

    updateImages([...images, newImage]);
  };

  const handleRemoveImage = (idx) => {
    updateImages(images.filter((_, i) => i !== idx));
  };

  const handleMoveImage = (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= images.length) return;
    const newImages = [...images];
    [newImages[idx], newImages[newIdx]] = [newImages[newIdx], newImages[idx]];
    updateImages(newImages);
  };

  const handleCaptionChange = (idx, caption) => {
    const newImages = [...images];
    newImages[idx] = { ...newImages[idx], caption };
    updateImages(newImages);
  };

  const sidebarTitle = `${intl.formatMessage(messages.imageGallery)} — ${intl.formatMessage(messages.imageCount, { count: images.length })}`;

  return (
    <>
      <div className="block image-gallery-edit">
        {images.length > 0 && (
          <div
            className="image-gallery-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${columns}, 1fr)`,
              gap,
            }}
          >
            {images.map((img, idx) => (
              <div key={idx} className="image-gallery-edit-item">
                <div className="image-gallery-edit-image">
                  {img.image_scales ? (
                    <Image
                      item={{
                        '@id': img.url,
                        image_field: img.image_field,
                        image_scales: img.image_scales,
                      }}
                      alt={img.alt || ''}
                      loading="lazy"
                      responsive={true}
                    />
                  ) : img.url ? (
                    <img
                      src={
                        isInternalURL(img.url)
                          ? `${flattenToAppURL(img.url)}/@@images/image/preview`
                          : img.url
                      }
                      alt={img.alt || ''}
                    />
                  ) : null}
                  <div className="image-gallery-edit-controls">
                    {idx > 0 && (
                      <Button
                        icon="arrow left"
                        size="mini"
                        onClick={() => handleMoveImage(idx, -1)}
                        title={intl.formatMessage(messages.moveLeft)}
                      />
                    )}
                    {idx < images.length - 1 && (
                      <Button
                        icon="arrow right"
                        size="mini"
                        onClick={() => handleMoveImage(idx, 1)}
                        title={intl.formatMessage(messages.moveRight)}
                      />
                    )}
                    <Button
                      icon="trash"
                      size="mini"
                      color="red"
                      onClick={() => handleRemoveImage(idx)}
                      title={intl.formatMessage(messages.removeImage)}
                    />
                  </div>
                </div>
                {editingCaption === idx ? (
                  <input
                    className="image-gallery-caption-input"
                    type="text"
                    value={img.caption || ''}
                    placeholder={intl.formatMessage(messages.captionPlaceholder)}
                    onChange={(e) => handleCaptionChange(idx, e.target.value)}
                    onBlur={() => setEditingCaption(null)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') setEditingCaption(null);
                    }}
                    autoFocus
                  />
                ) : (
                  <div
                    className="image-gallery-caption-display"
                    onClick={() => setEditingCaption(idx)}
                  >
                    {img.caption || intl.formatMessage(messages.clickToAddCaption)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="image-gallery-add">
          <ImageInput
            onChange={handleAddImage}
            block={block}
            id={`${block}-add`}
            objectBrowserPickerType="image"
          />
        </div>
      </div>

      <SidebarPortal selected={selected}>
        <InlineForm
          schema={ImageGallerySchema({ intl })}
          title={sidebarTitle}
          onChangeField={(id, value) =>
            onChangeBlock(block, { ...data, [id]: value })
          }
          formData={data}
        />
        {images.length > 0 && (
          <Segment.Group raised>
            <header className="header pulled">
              <h2>{intl.formatMessage(messages.images)}</h2>
            </header>
            {images.map((img, idx) => (
              <Segment key={idx} className="image-gallery-sidebar-item">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <img
                    src={
                      isInternalURL(img.url)
                        ? `${flattenToAppURL(img.url)}/@@images/image/icon`
                        : img.url
                    }
                    alt=""
                    style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 2 }}
                  />
                  <div style={{ flex: 1, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {img.caption || img.alt || img.url?.split('/').pop() || intl.formatMessage(messages.imageN, { n: idx + 1 })}
                  </div>
                  <Button.Group size="mini">
                    {idx > 0 && (
                      <Button icon="arrow up" onClick={() => handleMoveImage(idx, -1)} title={intl.formatMessage(messages.moveUp)} />
                    )}
                    {idx < images.length - 1 && (
                      <Button icon="arrow down" onClick={() => handleMoveImage(idx, 1)} title={intl.formatMessage(messages.moveDown)} />
                    )}
                    <Button icon="trash" onClick={() => handleRemoveImage(idx)} title={intl.formatMessage(messages.removeImage)} />
                  </Button.Group>
                </div>
              </Segment>
            ))}
          </Segment.Group>
        )}
      </SidebarPortal>
    </>
  );
};

export default withBlockExtensions(ImageGalleryEdit);
